/**
 * Accounting API Module
 * Bọc toàn bộ endpoint /accounting/* (công nợ phải thu KH + công nợ phải trả NCC).
 * Backend: app/api/accounting.py + app/api/accounting_payables.py.
 */

import apiClient, { extractData } from '../api-client';

export const accountingApi = {
  // ------------------------------------------------------ Công nợ phải thu (KH)

  /**
   * Danh sách công nợ theo từng đơn hàng (có phân trang + summary).
   * @param {Object} params - { page, size, search, status, from_date, to_date, overdue_only }
   */
  getReceivables(params) {
    return apiClient.get('/accounting/receivables', { params }).then(extractData);
  },

  /**
   * Cùng dữ liệu với getReceivables, gộp theo khách hàng — không phân trang.
   * @param {Object} params - { search, status, from_date, to_date, overdue_only }
   */
  getReceivablesByCustomer(params) {
    return apiClient
      .get('/accounting/receivables/by-customer', { params })
      .then(extractData);
  },

  /**
   * Chi tiết công nợ một đơn kèm sổ phiếu thu.
   * @param {number} orderId
   */
  getReceivableDetail(orderId) {
    return apiClient.get(`/accounting/receivables/${orderId}`).then(extractData);
  },

  /**
   * Ghi một phiếu thu cho đơn hàng.
   * @param {number} orderId
   * @param {Object} data - { amount, paid_at, method, note }
   */
  createPayment(orderId, data) {
    return apiClient
      .post(`/accounting/receivables/${orderId}/payments`, data)
      .then(extractData);
  },

  /**
   * Sửa một phiếu thu.
   * @param {number} orderId
   * @param {number} paymentId
   * @param {Object} data
   */
  updatePayment(orderId, paymentId, data) {
    return apiClient
      .put(`/accounting/receivables/${orderId}/payments/${paymentId}`, data)
      .then(extractData);
  },

  /**
   * Xoá một phiếu thu.
   * @param {number} orderId
   * @param {number} paymentId
   */
  deletePayment(orderId, paymentId) {
    return apiClient
      .delete(`/accounting/receivables/${orderId}/payments/${paymentId}`)
      .then(extractData);
  },

  /**
   * Đặt/xoá hạn thanh toán của một đơn (due_date=null để xoá hạn đã đặt).
   * @param {number} orderId
   * @param {Object} data - { due_date }
   */
  updateDueDate(orderId, data) {
    return apiClient
      .patch(`/accounting/receivables/${orderId}/due-date`, data)
      .then(extractData);
  },

  // -------------------------------------------------- Công nợ phải trả (NCC)

  /**
   * Danh mục nhà cung cấp — không phân trang.
   * @param {Object} params - { search, include_inactive }
   */
  getSuppliers(params) {
    return apiClient.get('/accounting/suppliers', { params }).then(extractData);
  },

  createSupplier(data) {
    return apiClient.post('/accounting/suppliers', data).then(extractData);
  },

  updateSupplier(id, data) {
    return apiClient.put(`/accounting/suppliers/${id}`, data).then(extractData);
  },

  deleteSupplier(id) {
    return apiClient.delete(`/accounting/suppliers/${id}`).then(extractData);
  },

  /**
   * Danh sách hoá đơn NCC (có phân trang + summary).
   * @param {Object} params - { page, size, supplier_id, from_date, to_date, overdue_only, status }
   */
  getSupplierInvoices(params) {
    return apiClient.get('/accounting/supplier-invoices', { params }).then(extractData);
  },

  createSupplierInvoice(data) {
    return apiClient.post('/accounting/supplier-invoices', data).then(extractData);
  },

  /**
   * Chi tiết một hoá đơn NCC kèm sổ phiếu chi.
   * @param {number} invoiceId
   */
  getSupplierInvoiceDetail(invoiceId) {
    return apiClient.get(`/accounting/supplier-invoices/${invoiceId}`).then(extractData);
  },

  updateSupplierInvoice(id, data) {
    return apiClient.put(`/accounting/supplier-invoices/${id}`, data).then(extractData);
  },

  deleteSupplierInvoice(id) {
    return apiClient.delete(`/accounting/supplier-invoices/${id}`).then(extractData);
  },

  /**
   * Ghi một phiếu chi cho hoá đơn NCC.
   * @param {number} invoiceId
   * @param {Object} data - { amount, paid_at, method, note }
   */
  createSupplierPayment(invoiceId, data) {
    return apiClient
      .post(`/accounting/supplier-invoices/${invoiceId}/payments`, data)
      .then(extractData);
  },

  deleteSupplierPayment(invoiceId, paymentId) {
    return apiClient
      .delete(`/accounting/supplier-invoices/${invoiceId}/payments/${paymentId}`)
      .then(extractData);
  },

  // -------------------------------------------- Thanh toán đơn hàng (tab mới)

  /**
   * Danh sách MỌI đơn kèm số tiền trên đơn, tổng phiếu thu và chênh lệch.
   * Khác getReceivables: không lọc theo sổ công nợ, nên thấy cả đơn Nháp/Lên Market
   * — đúng giai đoạn khách đặt cọc.
   * @param {Object} params - { page, size, search, payment_status, only_mismatched }
   */
  getOrderPayments(params) {
    return apiClient.get('/accounting/order-payments', { params }).then(extractData);
  },

  /**
   * Ghi số tiền đã thanh toán + trạng thái cho một đơn, tuỳ chọn sinh phiếu thu.
   * @param {number} orderId
   * @param {Object} payload - { paid_amount, payment_status, create_receipt,
   *                             receipt_method, receipt_paid_at, receipt_note }
   */
  updateOrderPayment(orderId, payload) {
    return apiClient
      .patch(`/accounting/order-payments/${orderId}`, payload)
      .then(extractData);
  },

  // ------------------------------------------------------------------ Export

  /**
   * Xuất file Excel công nợ (2 sheet: CongNo_KhachHang + CongNo_NhaCungCap).
   * Theo đúng pattern tải file đã dùng ở dashboardApi.exportRevenueCsv:
   * gọi bằng responseType 'blob', component tự dựng URL tạm + <a download> để tải.
   * @param {Object} params - { search, status, from_date, to_date, overdue_only }
   * @returns {Promise<Blob>}
   */
  exportDebtWorkbook(params) {
    return apiClient
      .get('/accounting/export.xlsx', { params, responseType: 'blob' })
      .then((res) => res.data);
  },
};
